'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Pusher from 'pusher-js'

export default function PollWidget({ poll, currentUserId }: { poll: any, currentUserId: string | null }) {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 1. Hold votes in local state so Pusher can update them instantly
  const [votes, setVotes] = useState<any[]>(poll.votes || [])

  // 2. Listen for live updates via Pusher
  useEffect(() => {
    let pusherClient: any = null
    let channel: any = null

    try {
      const pusherKey = process.env.NEXT_PUBLIC_PUSHER_KEY
      if (pusherKey) {
        pusherClient = new Pusher(pusherKey, { cluster: 'eu' })
        
        // Subscribe to a unique channel for this specific poll
        channel = pusherClient.subscribe(`poll-${poll.id}`)

        channel.bind('new-vote', (newVote: any) => {
          setVotes(prev => {
            // Prevent duplicates just in case
            if (prev.some(v => v.id === newVote.id)) return prev
            return [...prev, newVote]
          })
        })
      }
    } catch (err) {
      console.warn("Pusher poll connection failed:", err)
    }

    return () => {
      if (pusherClient && channel) {
        pusherClient.unsubscribe(`poll-${poll.id}`)
      }
    }
  }, [poll.id])

  // 3. Derive calculations from the LIVE state instead of the static prop
  const userAlreadyVoted = votes.some((v: any) => v.userId === currentUserId)
  const totalVotes = votes.length

  const handleVote = async (optionId: string) => {
    if (!currentUserId) {
      alert('You must be logged in to vote.')
      return
    }
    setLoading(true)
    setError('')

    try {
      const res = await fetch(`/api/polls/${poll.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionId })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to submit vote')

      // Note: We removed router.refresh() here because Pusher 
      // will now update the UI automatically for everyone!
      
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] shadow-xl space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <span>📊</span> {poll.question}
        </h3>
        <span className="text-[10px] font-mono text-slate-500 bg-slate-950 px-2.5 py-1 rounded-xl border border-slate-800 transition-all">
          {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
        </span>
      </div>

      {error && <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-xl">{error}</div>}

      <div className="space-y-2.5">
        {poll.options.map((option: any) => {
          // Calculate using the live state
          const optionVotes = votes.filter((v: any) => v.optionId === option.id).length
          const percentage = totalVotes > 0 ? Math.round((optionVotes / totalVotes) * 100) : 0

          return (
            <div key={option.id} className="relative overflow-hidden bg-slate-950 border border-slate-800 rounded-2xl p-3.5 transition-all">
              
              {/* Progress bar background for results */}
              {userAlreadyVoted && (
                <div 
                  className="absolute top-0 left-0 bottom-0 bg-indigo-600/15 border-r border-indigo-500/30 transition-all duration-700 ease-out" 
                  style={{ width: `${percentage}%` }}
                />
              )}

              <div className="relative z-10 flex items-center justify-between gap-4">
                <span className="text-xs font-medium text-slate-200">{option.text}</span>

                {userAlreadyVoted ? (
                  <span className="text-xs font-mono font-bold text-indigo-400 shrink-0 transition-all duration-700">
                    {percentage}%
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleVote(option.id)}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-1.5 rounded-xl text-xs font-bold transition-colors shadow-md disabled:opacity-50 shrink-0"
                  >
                    Vote
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}